'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, Cell, XAxis, YAxis } from 'recharts';

import { ChartContainer, ChartTooltip } from '@/components/ui/chart';
import { formatMoney } from "@/lib/money";
import { EmptyState } from './EmptyState';
import { formatBucketDate } from '../utils/formatBucketDate';

type RefundsByTypeEntry = {
    straightSale: { amount: number; count: number };
    rebill: { amount: number; count: number };
    upsell: { amount: number; count: number };
    other: { amount: number; count: number };
};

export type RefundsByTypeViewMode = 'timeSeries' | 'aggregated';

interface RefundsByTypeChartProps {
    data: Map<string, RefundsByTypeEntry> | undefined;
    currency: string;
    className?: string;
    viewMode?: RefundsByTypeViewMode;
    granularity?: 'daily' | 'weekly' | 'monthly';
}

// Helper to get week start date (Monday)
function getWeekStart(date: Date): string {
    const d = new Date(date);
    const day = d.getUTCDay();
    const diff = d.getUTCDate() - day + (day === 0 ? -6 : 1);
    d.setUTCDate(diff);
    return d.toISOString().split('T')[0];
}

// Helper to get month start date
function getMonthStart(date: Date): string {
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-01`;
}

const REFUND_TYPE_COLOR_KEYS: Record<string, string> = {
    'straightSale': 'var(--chart-1)',
    'rebill': 'var(--chart-2)',
    'upsell': 'var(--chart-3)',
    'other': 'var(--chart-5)',
};

export function RefundsByTypeChart({ data, currency, className = '', viewMode = 'timeSeries', granularity = 'daily' }: RefundsByTypeChartProps) {
    const { t } = useTranslation('analytics');
    const timeSeriesData = React.useMemo(() => {
        if (!data || data.size === 0) return [];

        // Get raw daily data
        const rawData = Array.from(data.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, entry]) => ({
                date,
                straightSale: entry.straightSale.amount,
                rebill: entry.rebill.amount,
                upsell: entry.upsell.amount,
                other: entry.other.amount,
                straightSaleCount: entry.straightSale.count,
                rebillCount: entry.rebill.count,
                upsellCount: entry.upsell.count,
                otherCount: entry.other.count,
            }));

        // If daily, return as-is
        if (granularity === 'daily') return rawData;

        // Group by granularity
        const grouped: Record<string, typeof rawData> = {};
        rawData.forEach(entry => {
            const d = new Date(entry.date);
            const key = granularity === 'weekly' ? getWeekStart(d) : getMonthStart(d);
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(entry);
        });

        // Aggregate grouped data
        return Object.entries(grouped)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, entries]) => ({
                date,
                straightSale: entries.reduce((sum, e) => sum + e.straightSale, 0),
                rebill: entries.reduce((sum, e) => sum + e.rebill, 0),
                upsell: entries.reduce((sum, e) => sum + e.upsell, 0),
                other: entries.reduce((sum, e) => sum + e.other, 0),
                straightSaleCount: entries.reduce((sum, e) => sum + e.straightSaleCount, 0),
                rebillCount: entries.reduce((sum, e) => sum + e.rebillCount, 0),
                upsellCount: entries.reduce((sum, e) => sum + e.upsellCount, 0),
                otherCount: entries.reduce((sum, e) => sum + e.otherCount, 0),
            }));
    }, [data, granularity]);

    const aggregatedData = React.useMemo(() => {
        if (!data || data.size === 0) return [];
        const totals = { straightSale: 0, rebill: 0, upsell: 0, other: 0, straightSaleCount: 0, rebillCount: 0, upsellCount: 0, otherCount: 0 };
        data.forEach((entry) => {
            totals.straightSale += entry.straightSale.amount;
            totals.rebill += entry.rebill.amount;
            totals.upsell += entry.upsell.amount;
            totals.other += entry.other.amount;
            totals.straightSaleCount += entry.straightSale.count;
            totals.rebillCount += entry.rebill.count;
            totals.upsellCount += entry.upsell.count;
            totals.otherCount += entry.other.count;
        });
        const result = [
            { name: t('charts.straightSale'), key: 'straightSale', value: totals.straightSale, count: totals.straightSaleCount },
            { name: t('charts.rebill'), key: 'rebill', value: totals.rebill, count: totals.rebillCount },
            { name: t('charts.upsell'), key: 'upsell', value: totals.upsell, count: totals.upsellCount },
        ];
        if (totals.other > 0) {
            result.push({ name: t('charts.other'), key: 'other', value: totals.other, count: totals.otherCount });
        }
        return result.filter(item => item.value > 0).sort((a, b) => b.value - a.value);
    }, [data]);

    const chartData = viewMode === 'aggregated' ? aggregatedData : timeSeriesData;

    const formatYAxisValue = (value: number) => {
        const majorUnits = value / 100;
        if (majorUnits >= 1000000) return `${(majorUnits / 1000000).toFixed(1)}M`;
        if (majorUnits >= 1000) return `${(majorUnits / 1000).toFixed(1)}K`;
        return `${majorUnits.toFixed(0)}`;
    };

    const labelFormatter = (label: string) => {
        try {
            return formatBucketDate(label, { month: 'short', day: 'numeric' });
        } catch {
            return label;
        }
    };

    const TimeSeriesCustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload || payload.length === 0) return null;

        const total = payload.reduce((sum: number, entry: any) => sum + (Number(entry.value) || 0), 0);
        const reversed = [...payload].reverse();
        const dataItem = payload[0]?.payload;

        return (
            <div className="bg-card p-3 shadow-lg rounded border border-border">
                <p className="text-card-foreground font-medium mb-2">{labelFormatter(String(label))}</p>
                <div className="space-y-1">
                    {reversed.map((entry: any, idx: number) => {
                        const value = Number(entry.value) || 0;
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                        const countKey = `${entry.dataKey}Count`;
                        const count = dataItem?.[countKey] || 0;

                        return (
                            <div key={idx} className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded" style={{ backgroundColor: entry.color }} />
                                    <span className="text-sm text-muted-foreground">{entry.name}:</span>
                                </div>
                                <div className="text-right">
                                    <span className="text-sm font-medium text-card-foreground">{formatMoney(value, currency)}</span>
                                    <span className="text-xs text-muted-foreground ml-2">({percentage}%)</span>
                                    {count > 0 && (
                                        <span className="text-xs text-muted-foreground ml-1">• {count}</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    <div className="pt-2 mt-2 border-t border-border">
                        <div className="flex items-center justify-between gap-4">
                            <span className="text-sm font-medium text-muted-foreground">{t('charts.total')}:</span>
                            <span className="text-sm font-semibold text-card-foreground">{formatMoney(total, currency)}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const AggregatedCustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload || payload.length === 0) return null;
        const item = payload[0].payload;
        return (
            <div className="bg-card p-3 shadow-lg rounded border border-border">
                <p className="text-card-foreground font-medium mb-2">{item.name}</p>
                <div className="space-y-1">
                    <div className="flex items-center justify-between gap-4">
                        <span className="text-sm text-muted-foreground">{t('charts.amount')}:</span>
                        <span className="text-sm font-medium text-card-foreground">{formatMoney(item.value, currency)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                        <span className="text-sm text-muted-foreground">{t('charts.count')}:</span>
                        <span className="text-sm font-medium text-card-foreground">{item.count.toLocaleString()}</span>
                    </div>
                </div>
            </div>
        );
    };

    if (!chartData.length) {
        return <EmptyState message={t('charts.noRefunds')} className={className} />;
    }

    // Check if we have any 'other' data to display (time series only)
    const hasOther = viewMode === 'timeSeries' && timeSeriesData.some(d => d.other > 0);

    if (viewMode === 'aggregated') {
        return (
            <div className={`h-full ${className}`}>
                <ChartContainer
                    config={{
                        straightSale: { label: t('charts.straightSale'), color: 'var(--chart-1)' },
                        rebill: { label: t('charts.rebill'), color: 'var(--chart-2)' },
                        upsell: { label: t('charts.upsell'), color: 'var(--chart-3)' },
                        other: { label: t('charts.other'), color: 'var(--chart-5)' },
                    }}
                    className="w-full h-full"
                >
                    <BarChart
                        data={aggregatedData}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                    >
                        <XAxis type="number" tickFormatter={formatYAxisValue} />
                        <YAxis
                            type="category"
                            dataKey="name"
                            width={90}
                            tick={{ fontSize: 12 }}
                            tickLine={false}
                            axisLine={false}
                        />
                        <ChartTooltip cursor={false} content={<AggregatedCustomTooltip />} />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {aggregatedData.map((entry) => (
                                <Cell key={entry.name} fill={REFUND_TYPE_COLOR_KEYS[entry.key] || 'var(--chart-1)'} />
                            ))}
                        </Bar>
                    </BarChart>
                </ChartContainer>
            </div>
        );
    }

    return (
        <div className={`h-full ${className}`}>
            <ChartContainer
                config={{
                    straightSale: { label: t('charts.straightSale'), color: 'var(--chart-1)' },
                    rebill: { label: t('charts.rebill'), color: 'var(--chart-2)' },
                    upsell: { label: t('charts.upsell'), color: 'var(--chart-3)' },
                    ...(hasOther ? { other: { label: t('charts.other'), color: 'var(--chart-5)' } } : {}),
                }}
                className="w-full h-full"
            >
                <BarChart accessibilityLayer data={timeSeriesData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                    <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={labelFormatter} />
                    <YAxis tickFormatter={formatYAxisValue} />
                    <ChartTooltip cursor={false} content={<TimeSeriesCustomTooltip />} />
                    {hasOther && (
                        <Bar dataKey="other" name={t('charts.other')} stackId="refunds" fill="var(--color-other)" radius={[0, 0, 0, 0]} />
                    )}
                    <Bar dataKey="upsell" name={t('charts.upsell')} stackId="refunds" fill="var(--color-upsell)" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="rebill" name={t('charts.rebill')} stackId="refunds" fill="var(--color-rebill)" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="straightSale" name={t('charts.straightSale')} stackId="refunds" fill="var(--color-straightSale)" radius={[4, 4, 0, 0]} />
                </BarChart>
            </ChartContainer>
        </div>
    );
}
